import React, { useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Loader2, CheckCircle2, Bell, BellOff, MapPin, Monitor } from "lucide-react";
import { pushSupported, getPushStatus, subscribePush, unsubscribePush } from "@/lib/push";

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneStep, setPhoneStep] = useState("idle"); // idle | sent
  const [phoneCode, setPhoneCode] = useState("");

  React.useEffect(() => {
    api.get("/wallet/profile").then(({ data }) => {
      setProfile(data);
      setPhoneInput(data.phone || "");
    });
  }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const confirm = async () => {
    if (code.length < 6) { toast.error("Enter the 6-digit code."); return; }
    setLoading(true);
    try {
      await api.post("/auth/2fa/confirm", { code });
      await refreshUser();
      setSetup(null); setCode("");
      toast.success("2FA enabled. Your account is more secure.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const disable = async () => {
    if (code.length < 6) { toast.error("Enter your 6-digit 2FA code to disable."); return; }
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      await refreshUser();
      setCode("");
      toast.success("2FA disabled.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <div className="text-eyebrow text-[#4F4D45]">Account</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Settings</h1>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        {/* Profile card */}
        <div className="fv-card p-6 md:p-8">
          <h2 className="font-display text-2xl font-medium">Profile</h2>
          <div className="mt-6 flex items-center gap-4">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt={user.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-white/10" data-testid="settings-avatar" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#A85943] text-white flex items-center justify-center font-display text-2xl font-semibold" data-testid="settings-avatar">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-display text-xl font-medium">{profile?.name || user?.name}</div>
              <div className="text-sm text-[#4F4D45]">{profile?.email || user?.email}</div>
              <div className="text-xs text-[#4F4D45] mt-1">Profile photo is the selfie you submitted at KYC.</div>
            </div>
          </div>
          <div className="mt-6 space-y-3 text-sm">
            <Row k="Name" v={profile?.name || user?.name} />
            <Row k="Email" v={profile?.email || user?.email} />
            <Row k="Account type" v={profile?.account_type || user?.account_type} />
            <Row k="Account number" v={<span className="font-mono">{profile?.account_number || "—"}</span>} />
            <Row k="Wallet ID" v={<span className="font-mono">{profile?.wallet_id || user?.wallet_id}</span>} />
            {profile?.business_name && <Row k="Business" v={profile.business_name} />}
            <Row k="Date of birth" v={profile?.date_of_birth || "—"} />
            <Row k="Phone" v={
              profile?.phone ? (
                <span className="inline-flex items-center gap-2">
                  {profile.phone}
                  {profile.phone_verified
                    ? <span className="text-[#264C39] text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> verified</span>
                    : <span className="text-[#8A4632] text-xs">unverified</span>}
                </span>
              ) : "—"
            } />
            <Row k="Address" v={profile?.address ? [profile.address.line1, profile.address.city, profile.address.state, profile.address.country].filter(Boolean).join(", ") : "—"} />
            <Row k="Email verified" v={profile?.email_verified ? <span className="text-[#264C39] inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Yes</span> : "No"} />
            <Row k="KYC status" v={profile?.kyc_status || "—"} />
          </div>

          {/* Phone verification */}
          {profile && !profile.phone_verified && (
            <div className="mt-6 pt-5 border-t border-[#D2CDBE]">
              <Label className="text-eyebrow text-[#4F4D45]">Verify phone via SMS</Label>
              {phoneStep === "idle" ? (
                <div className="mt-2 flex gap-2">
                  <Input value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)} placeholder="+1 555 010 0123" data-testid="settings-phone-input" />
                  <Button
                    onClick={async () => {
                      if (!phoneInput.trim()) { toast.error("Enter a phone number first."); return; }
                      setPhoneSending(true);
                      try {
                        await api.post("/auth/phone/send", { phone: phoneInput.trim() });
                        setPhoneStep("sent");
                        toast.success("SMS code sent.");
                      } catch (e) { toast.error(formatApiError(e)); }
                      finally { setPhoneSending(false); }
                    }}
                    disabled={phoneSending}
                    className="bg-[#1B3526] hover:bg-[#0F2517]"
                    data-testid="settings-phone-send"
                  >
                    {phoneSending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}
                  </Button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <Input value={phoneCode} onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="font-mono text-center tracking-[0.3em]" data-testid="settings-phone-code" />
                  <Button
                    onClick={async () => {
                      try {
                        await api.post("/auth/phone/verify", { phone: phoneInput.trim(), code: phoneCode });
                        toast.success("Phone verified.");
                        setPhoneStep("idle"); setPhoneCode("");
                        const { data } = await api.get("/wallet/profile");
                        setProfile(data);
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="bg-[#1B3526] hover:bg-[#0F2517]"
                    data-testid="settings-phone-verify"
                  >
                    Verify
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 2FA card */}
        <div className="fv-card p-6 md:p-8">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-2xl font-medium">Two-factor authentication</h2>
            {user?.two_factor_enabled ? (
              <span className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#264C39]/10 text-[#264C39]" data-testid="2fa-status-on"><ShieldCheck className="w-3.5 h-3.5" /> Active</span>
            ) : (
              <span className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#B58957]/20 text-[#8A4632]" data-testid="2fa-status-off"><ShieldAlert className="w-3.5 h-3.5" /> Not active</span>
            )}
          </div>
          <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">
            {user?.two_factor_enabled
              ? "TOTP is active. Sign-in requires a code from your authenticator app."
              : "Without 2FA, we send a one-time code by email at every login. Add an authenticator app for stronger protection."}
          </p>

          {!user?.two_factor_enabled && !setup && (
            <Button onClick={startSetup} disabled={loading} className="mt-6 bg-[#1B3526] hover:bg-[#0F2517]" data-testid="2fa-start-button">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set up authenticator app"}
            </Button>
          )}

          {!user?.two_factor_enabled && setup && (
            <div className="mt-6 space-y-4">
              <p className="text-sm text-[#4F4D45]">Scan this QR with Google Authenticator, Authy, or 1Password:</p>
              <img src={setup.qr_code} alt="QR" className="w-44 h-44 border border-[#D2CDBE] rounded-xl p-2 bg-white" data-testid="2fa-qr" />
              <div className="text-xs text-[#4F4D45]">Or enter this key manually: <code className="font-mono text-[#0A0A08] break-all">{setup.secret}</code></div>
              <div>
                <Label className="text-eyebrow text-[#4F4D45]">Enter the 6-digit code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="mt-1 text-xl font-mono tracking-[0.4em] text-center" data-testid="2fa-confirm-code" />
              </div>
              <Button onClick={confirm} disabled={loading} className="bg-[#1B3526] hover:bg-[#0F2517]" data-testid="2fa-confirm-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activate 2FA"}
              </Button>
            </div>
          )}

          {user?.two_factor_enabled && (
            <div className="mt-6 space-y-3">
              <Label className="text-eyebrow text-[#4F4D45]">Disable 2FA — enter current code</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="text-xl font-mono tracking-[0.4em] text-center" data-testid="2fa-disable-code" />
              <Button onClick={disable} disabled={loading} variant="outline" className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white" data-testid="2fa-disable-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Disable 2FA"}
              </Button>
            </div>
          )}
        </div>

        <PreferencesSection />
        <PushSection />
        <SignInActivitySection />
      </div>
    </AppLayout>
  );
}

function PreferencesSection() {
  const [prefs, setPrefs] = React.useState(null);
  const [locale, setLocale] = React.useState("");
  const [currency, setCurrency] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get("/auth/preferences").then(({ data }) => {
      setPrefs(data);
      setLocale(data.locale);
      setCurrency(data.preferred_currency);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/preferences", { locale, preferred_currency: currency });
      toast.success("Preferences saved. Future receipts will use the new locale.");
    } catch (e) {
      toast.error("Couldn't save preferences.");
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;
  return (
    <div className="fv-card p-6 md:p-8" data-testid="settings-preferences">
      <div className="text-eyebrow text-[#4F4D45]">Localization</div>
      <h2 className="font-display text-xl md:text-2xl font-bold mt-1">Language & currency</h2>
      <p className="text-sm text-[#4F4D45] mt-2">
        Changes apply to receipts, notifications, and date/number formatting across the app.
      </p>
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        <div>
          <Label className="text-xs text-[#4F4D45]">Language / region</Label>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            data-testid="settings-locale-select"
            className="mt-1 w-full px-3 py-2 border border-[#D2CDBE] rounded-md bg-white text-[#0A0A08] focus:outline-none focus:ring-2 focus:ring-[#1B3526]"
          >
            {(prefs.supported_locales || []).map((l) => (
              <option key={l.code} value={l.code}>
                {`${l.name} (${l.code})`}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs text-[#4F4D45]">Preferred display currency</Label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            data-testid="settings-currency-select"
            className="mt-1 w-full px-3 py-2 border border-[#D2CDBE] rounded-md bg-white text-[#0A0A08] focus:outline-none focus:ring-2 focus:ring-[#1B3526]"
          >
            {(prefs.supported_currencies || []).map((c) => (
              <option key={c.code} value={c.code}>
                {`${c.symbol} ${c.name} (${c.code})`}
              </option>
            ))}
          </select>
          <p className="text-xs text-[#4F4D45] mt-1">
            Note: your ledger balance is USD. This affects display only.
          </p>
        </div>
      </div>
      <div className="mt-6">
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
          data-testid="settings-preferences-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save preferences"}
        </Button>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-6 py-2 border-b border-[#D2CDBE] last:border-0">
      <div className="text-[#4F4D45]">{k}</div>
      <div className="text-right">{v}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Web Push section
// ---------------------------------------------------------------------------
function PushSection() {
  const [status, setStatus] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [optOut, setOptOut] = React.useState(false);

  const refresh = React.useCallback(async () => {
    try {
      const s = await getPushStatus();
      setStatus(s);
      const { data } = await api.get("/notifications/preferences");
      setOptOut(!!data.push_opt_out);
    } catch (e) {
      /* silent */
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Push notifications enabled on this device.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't enable push.");
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Push disabled on this device.");
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't disable push.");
    } finally {
      setBusy(false);
    }
  };

  const toggleOptOut = async (val) => {
    setBusy(true);
    try {
      await api.patch("/notifications/preferences", { push_opt_out: val });
      setOptOut(val);
      toast.success(val ? "Push muted globally for your account." : "Push re-enabled globally.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!pushSupported()) {
    return (
      <div className="fv-card p-6 md:p-8" data-testid="settings-push-unsupported">
        <div className="flex items-center gap-2 text-[#8A4632]">
          <BellOff className="w-5 h-5" />
          <h2 className="font-display text-2xl font-medium">Push notifications</h2>
        </div>
        <p className="text-sm text-[#4F4D45] mt-3 leading-relaxed">
          Your browser doesn't support Web Push. Try Chrome, Edge, Firefox, or Safari 16+ on macOS/iOS.
        </p>
      </div>
    );
  }

  return (
    <div className="fv-card p-6 md:p-8" data-testid="settings-push-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#264C39]" />
          <h2 className="font-display text-2xl font-medium">Push notifications</h2>
        </div>
        {status?.subscribed ? (
          <span className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#264C39]/10 text-[#264C39]" data-testid="push-status-on">
            <ShieldCheck className="w-3.5 h-3.5" /> On this device
          </span>
        ) : (
          <span className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#B58957]/20 text-[#8A4632]" data-testid="push-status-off">
            <BellOff className="w-3.5 h-3.5" /> Off
          </span>
        )}
      </div>
      <p className="text-sm text-[#4F4D45] mt-3 leading-relaxed">
        Get instant alerts for transfers, deposits, withdrawals, KYC updates,
        and new sign-ins — even when the FinVault tab isn't open.
      </p>

      <div className="mt-5 flex flex-wrap gap-3">
        {!status?.subscribed ? (
          <Button
            onClick={enable}
            disabled={busy || !status?.publicKey}
            className="bg-[#1B3526] hover:bg-[#0F2517] text-white"
            data-testid="push-enable-button"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Bell className="w-4 h-4 mr-2" />}
            Enable push on this device
          </Button>
        ) : (
          <Button
            onClick={disable}
            disabled={busy}
            variant="outline"
            className="border-[#992F2F] text-[#992F2F] hover:bg-[#992F2F] hover:text-white"
            data-testid="push-disable-button"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BellOff className="w-4 h-4 mr-2" />}
            Disable on this device
          </Button>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-[#D2CDBE]">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={optOut}
            onChange={(e) => toggleOptOut(e.target.checked)}
            disabled={busy}
            className="w-4 h-4 accent-[#1B3526]"
            data-testid="push-optout-checkbox"
          />
          <span className="text-sm text-[#4F4D45]">
            Mute all push notifications for my account (all devices)
          </span>
        </label>
      </div>

      {!status?.publicKey && (
        <p className="text-xs text-[#8A4632] mt-4">
          Push service isn't fully configured on the server yet. Please contact support if this persists.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent sign-in activity
// ---------------------------------------------------------------------------
function SignInActivitySection() {
  const [items, setItems] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api
      .get("/auth/sign-in-activity?limit=10")
      .then(({ data }) => {
        setItems(data.activity || []);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="fv-card p-6 md:p-8 lg:col-span-2" data-testid="settings-signin-activity">
      <div className="flex items-center gap-2">
        <Monitor className="w-5 h-5 text-[#264C39]" />
        <h2 className="font-display text-2xl font-medium">Recent sign-ins</h2>
      </div>
      <p className="text-sm text-[#4F4D45] mt-2 leading-relaxed">
        Last 10 successful sign-ins to your account. If you spot something unfamiliar, reset your password immediately.
      </p>
      <div className="mt-5">
        {loading ? (
          <div className="text-sm text-[#4F4D45]">Loading…</div>
        ) : items && items.length > 0 ? (
          <div className="space-y-2">
            {items.map((it) => (
              <div
                key={it.id}
                className="flex items-center justify-between gap-4 py-3 px-4 rounded-xl bg-[#F0ECE0]/60 border border-[#D2CDBE]"
                data-testid="signin-activity-row"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Monitor className="w-5 h-5 text-[#264C39]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#0A0A08] truncate">
                      {it.device || "Unknown device"}
                      {it.new_device && (
                        <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-[#B58957]">
                          new
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#4F4D45] flex items-center gap-1.5 mt-0.5">
                      <MapPin className="w-3 h-3" /> {it.ip}
                      <span className="ml-1">· {it.method}</span>
                    </div>
                  </div>
                </div>
                <div className="text-xs text-[#4F4D45] whitespace-nowrap">
                  {it.at ? new Date(it.at).toLocaleString() : "—"}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-[#4F4D45]">No sign-in activity recorded yet.</div>
        )}
      </div>
    </div>
  );
}
